import {
	usePublicDataRemovalRequestConfirm,
	usePublicDataRemovalRequestShow,
} from '@jobilla/nexus-sdk/lib/hooks/usePublicDataRemovalRequest'
import { createFileRoute } from '@tanstack/react-router'
import type { AxiosError } from 'axios'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/removal-requests/$token')({
	head: () => ({
		meta: [{ title: 'Data removal request · Jobilla' }],
	}),
	component: RemovalRequestPage,
})

function RemovalRequestPage() {
	const { token } = Route.useParams()
	const { t } = useTranslation('common')
	const [confirmed, setConfirmed] = useState(false)

	const showQuery = usePublicDataRemovalRequestShow(token, { retry: 0 })
	const confirmMutation = usePublicDataRemovalRequestConfirm({
		onSuccess: () => setConfirmed(true),
	})

	if (showQuery.isLoading) {
		return (
			<CenteredScreen>
				<p className="text-sm text-grey-500">{t('removal_request.loading')}</p>
			</CenteredScreen>
		)
	}

	if (showQuery.isError) {
		const status = (showQuery.error as AxiosError)?.response?.status
		if (status === 404) {
			return (
				<CenteredScreen>
					<h1 className="text-lg font-medium text-grey-800">{t('removal_request.not_found_title')}</h1>
					<p className="max-w-sm text-sm text-grey-500">{t('removal_request.not_found_body')}</p>
				</CenteredScreen>
			)
		}
		return (
			<CenteredScreen>
				<p className="text-sm text-grey-500">{t('removal_request.error')}</p>
			</CenteredScreen>
		)
	}

	const data = showQuery.data
	if (!data) {
		return (
			<CenteredScreen>
				<p className="text-sm text-grey-500">{t('removal_request.loading')}</p>
			</CenteredScreen>
		)
	}

	if (confirmed) {
		return (
			<CenteredScreen>
				<CompanyLogo url={data.companyLogoUrl} name={data.companyName} />
				<h1 className="text-lg font-medium text-grey-800">{t('removal_request.confirmed_title')}</h1>
				<p className="max-w-sm text-sm text-grey-500">{t('removal_request.confirmed_body')}</p>
			</CenteredScreen>
		)
	}

	if (data.status !== 'pending') {
		return (
			<CenteredScreen>
				<CompanyLogo url={data.companyLogoUrl} name={data.companyName} />
				<p className="max-w-sm text-sm text-grey-500">{data.message ?? t('removal_request.already_handled')}</p>
			</CenteredScreen>
		)
	}

	const isConflict = confirmMutation.isError
		&& (confirmMutation.error as AxiosError)?.response?.status === 409

	return (
		<CenteredScreen>
			<CompanyLogo url={data.companyLogoUrl} name={data.companyName} />
			<h1 className="text-lg font-medium text-grey-800">{t('removal_request.confirm_heading')}</h1>
			<p className="max-w-sm text-sm text-grey-500">
				{t('removal_request.confirm_body', { companyName: data.companyName ?? 'the company' })}
			</p>

			<button
				type="button"
				disabled={confirmMutation.isPending}
				onClick={() => confirmMutation.mutate({ token })}
				className="mt-4 rounded-lg bg-grey-800 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-grey-700 disabled:opacity-50"
			>
				{confirmMutation.isPending ? '…' : t('removal_request.confirm_button')}
			</button>

			{isConflict ? <p className="mt-2 text-sm text-red-500">{t('removal_request.conflict')}</p> : null}
			{confirmMutation.isError && !isConflict
				? <p className="mt-2 text-sm text-red-500">{t('removal_request.error')}</p>
				: null}
		</CenteredScreen>
	)
}

function CenteredScreen({ children }: { children: React.ReactNode }) {
	return (
		<div className="flex min-h-screen flex-col items-center justify-center gap-2 px-6 text-center">
			{children}
		</div>
	)
}

function CompanyLogo({ url, name }: { url: string | null; name: string | null }) {
	if (!url) {
		return null
	}
	return (
		<img
			src={url}
			alt={name ?? ''}
			className="mb-2 h-12 w-12 rounded-full object-cover"
		/>
	)
}
